/**
 * 로어북(월드 인포) 스캐너 — 결정 #12. 최근 메시지 창(scanDepth)을 키워드로
 * 스캔해 활성화할 엔트리를 고른다. 결정 #12대로 6s+ 보호를 위해 호출부에서
 * Web Worker로 오프로딩할 수 있도록 순수 함수로 둔다(부수효과 없음).
 *
 * 매칭 규칙은 ST/Risu와 정렬(`docs/risu-migration.md` §2): primary 키 + 선택적
 * secondary 키를 selectiveLogic으로 결합한다. `constant` 엔트리는 스캔과 무관히
 * 항상 활성화된다.
 *
 * 추가 동작(결정 #12):
 *  - **재귀 스캔**: book.recursiveScanning이면 활성화된 엔트리의 내용이 스캔
 *    버퍼에 더해져 추가 엔트리를 트리거한다. 무한 루프/6s+ DoS 방어로 패스 상한.
 *  - **토큰 예산**: book.tokenBudget이 있으면 그 book의 활성 엔트리를 우선순위
 *    (insertionOrder) 높은 순으로 채우다 예산 초과 시 낮은 우선순위를 버린다.
 */
import type { Lorebook, LorebookEntry, LorebookPosition } from "../domain/index.js";
import { heuristicTokenCount, type TokenCounter } from "./tokens.js";
import { makeSeededRandom } from "../script/policy.js";
import { measurePerformance } from "../perf/index.js";

/** 활성화된 한 엔트리(어느 lorebook에서 왔는지 추적). */
export interface ActivatedEntry {
  entry: LorebookEntry;
  lorebookId: string;
}

/** position별로 묶인 활성화 결과(파이프라인이 블록에 주입할 때 사용). */
export type ActivationByPosition = Record<LorebookPosition, ActivatedEntry[]>;

/** 재귀 스캔 패스 상한(무한 루프/6s+ DoS 방어, 결정 #12). */
const MAX_RECURSION_PASSES = 5;

function emptyByPosition(): ActivationByPosition {
  return {
    "before-char": [],
    "after-char": [],
    "before-an": [],
    "after-an": [],
    "at-depth": [],
  };
}

/** 한 엔트리가 스캔 텍스트에 매칭되는지 판정한다. */
function entryMatches(entry: LorebookEntry, haystack: string): boolean {
  if (entry.disabled) return false;
  if (entry.constant) return true;
  if (entry.keys.length === 0) return false;

  const text = entry.caseSensitive ? haystack : haystack.toLowerCase();
  const norm = (k: string) => (entry.caseSensitive ? k : k.toLowerCase());

  const primaryHits = entry.keys.map(norm).filter((k) => k && text.includes(k));
  const secondary = (entry.secondaryKeys ?? []).map(norm).filter(Boolean);
  const secondaryHits = secondary.filter((k) => text.includes(k));

  switch (entry.selectiveLogic) {
    case "and-any":
      // 1차 키 중 하나라도 매칭(ST 기본). secondary가 있으면 하나 이상 추가 매칭 요구.
      if (primaryHits.length === 0) return false;
      return secondary.length === 0 || secondaryHits.length > 0;
    case "and-all":
      if (primaryHits.length !== entry.keys.filter(Boolean).length) return false;
      return secondary.length === 0 || secondaryHits.length === secondary.length;
    case "not-any":
      // 1차는 매칭하되 secondary 중 하나라도 매칭되면 제외.
      return primaryHits.length > 0 && secondaryHits.length === 0;
    case "not-all":
      return primaryHits.length > 0 && secondaryHits.length < secondary.length;
    default:
      return primaryHits.length > 0;
  }
}

interface BookEntry {
  entry: LorebookEntry;
  lorebookId: string;
  /** 이 엔트리의 source book이 재귀 스캔 대상인지. */
  recursive: boolean;
}

/**
 * 주어진 lorebook들을 스캔 텍스트에 대해 평가해 활성 엔트리를 position별로
 * 모은다. 각 그룹은 insertionOrder 내림차순(높을수록 우선, 결정 #12)으로 정렬.
 *
 * @param scanText     최근 메시지 창을 이어붙인 텍스트(호출부에서 scanDepth 적용).
 * @param random       확률(probability) 판정용 0..1 난수원. 기본 Math.random.
 * @param tokenCounter 토큰 예산 측정용(기본 휴리스틱). 정확 토크나이저 주입 가능.
 */
export function scanLorebooks(
  lorebooks: Lorebook[],
  scanText: string,
  random: () => number = Math.random,
  tokenCounter: TokenCounter = heuristicTokenCount,
): ActivationByPosition {
  return measurePerformance(
    "retrieve_memory",
    {
      inputSize: lorebookScanInputSize(lorebooks, scanText),
      fileId: "lorebook-scan",
      packageId: lorebooks.map((book) => book.id).join(","),
      cache: "bypass",
      thread: "unknown",
      invalidationReason: "scan_text_or_lorebook_changed",
      detail: {
        lorebooks: lorebooks.length,
        entries: lorebooks.reduce((n, book) => n + book.entries.length, 0),
      },
    },
    () => scanLorebooksInternal(lorebooks, scanText, random, tokenCounter),
  );
}

function scanLorebooksInternal(
  lorebooks: Lorebook[],
  scanText: string,
  random: () => number,
  tokenCounter: TokenCounter,
): ActivationByPosition {
  // 모든 엔트리를 평탄화(book 메타 보존). 재귀 여부는 book 레벨.
  const all: BookEntry[] = [];
  const budgets = new Map<string, number | undefined>();
  let anyRecursive = false;
  for (const book of lorebooks) {
    budgets.set(book.id, book.tokenBudget);
    if (book.recursiveScanning) anyRecursive = true;
    for (const entry of book.entries) {
      all.push({ entry, lorebookId: book.id, recursive: book.recursiveScanning });
    }
  }

  const activated = new Set<LorebookEntry>();
  const activatedList: BookEntry[] = [];
  let buffer = scanText;

  // 활성화 시도(확률 게이트 포함). 성공 시 true.
  const tryActivate = (be: BookEntry): boolean => {
    if (activated.has(be.entry)) return false;
    if (!entryMatches(be.entry, buffer)) return false;
    // 확률 게이트(ST probability). `>=`라서 확률 0은 항상 제외, 100은 항상 포함.
    if (be.entry.probability != null && be.entry.probability < 100) {
      if (random() * 100 >= be.entry.probability) return false;
    }
    activated.add(be.entry);
    activatedList.push(be);
    return true;
  };

  // 첫 패스 + (재귀 켜진 경우) 추가 패스. 새 활성화가 없으면 종료.
  let pass = 0;
  let changed = true;
  while (changed && pass < MAX_RECURSION_PASSES) {
    changed = false;
    for (const be of all) {
      if (tryActivate(be)) {
        changed = true;
        // 재귀 가능한 book의 활성 내용은 스캔 버퍼에 더해 추가 트리거를 유발.
        if (be.recursive && be.entry.content) {
          buffer += "\n" + be.entry.content;
        }
      }
    }
    pass++;
    if (!anyRecursive) break; // 재귀 book이 없으면 한 패스로 충분.
  }

  // 토큰 예산 적용(per-book, 결정 #12). 높은 insertionOrder 우선 유지.
  const byBook = new Map<string, BookEntry[]>();
  for (const be of activatedList) {
    const arr = byBook.get(be.lorebookId);
    if (arr) arr.push(be);
    else byBook.set(be.lorebookId, [be]);
  }

  const result = emptyByPosition();
  for (const [bookId, entries] of byBook) {
    const budget = budgets.get(bookId);
    let kept = entries;
    if (budget != null && budget > 0) {
      const sorted = [...entries].sort(
        (a, b) => b.entry.insertionOrder - a.entry.insertionOrder,
      );
      kept = [];
      let used = 0;
      for (const be of sorted) {
        const cost = tokenCounter(be.entry.content);
        if (used + cost > budget) continue; // 예산 초과 엔트리는 버림
        kept.push(be);
        used += cost;
      }
    }
    for (const be of kept) {
      result[be.entry.position].push({ entry: be.entry, lorebookId: be.lorebookId });
    }
  }

  // position별 insertionOrder 내림차순 정렬(높을수록 우선, 결정 #12).
  for (const pos of Object.keys(result) as LorebookPosition[]) {
    result[pos].sort((a, b) => b.entry.insertionOrder - a.entry.insertionOrder);
  }
  return result;
}

function lorebookScanInputSize(lorebooks: Lorebook[], scanText: string): number {
  let size = scanText.length;
  for (const book of lorebooks) {
    for (const entry of book.entries) {
      size += entry.content.length;
      size += entry.keys.join("").length;
      size += entry.secondaryKeys?.join("").length ?? 0;
    }
  }
  return size;
}

/**
 * Web Worker 오프로딩용 직렬화 가능한 스캔 진입점(결정 #9/#12). 함수 인자(random/
 * tokenCounter)는 Worker 경계를 못 넘으므로, 대신 시드(숫자)를 받아 내부에서 시드 RNG를
 * 만들고 토큰 예산은 휴리스틱 카운터로 측정한다(Worker엔 정확 토크나이저를 두지 않음 —
 * 예산 트림은 근사로 충분하고, 메인스레드 부하를 더는 게 목적).
 *
 * 입력(lorebooks/scanText/seed)과 출력(ActivationByPosition)이 모두 structured-clone
 * 가능하므로 postMessage로 안전하게 오간다.
 */
export interface LorebookScanRequest {
  lorebooks: Lorebook[];
  scanText: string;
  /** 확률 게이트용 시드(같은 시드 → 같은 결과, 재현성). */
  seed: number;
}

export function scanLorebooksSeeded(req: LorebookScanRequest): ActivationByPosition {
  return scanLorebooks(req.lorebooks, req.scanText, makeSeededRandom(req.seed), heuristicTokenCount);
}
